import { RouteMiddlewareFactory } from '@atabix/vue-ui'
import { computed } from 'vue'
import { createRouter, createWebHistory, type RouteLocationRaw, type Router } from 'vue-router'

import { RouteName } from '@/enums'
import { InitializeAppMiddleware, InitializeSettingsMiddleware, ReadyMiddleware, VersionMiddleware } from '@/router/middleware'
import { clearDynamicImportReloadGuard, handleDynamicImportError } from '@/router/RouterErrorRecovery'
import { useAuthStore } from '@/stores/auth'
import NotFound from '@/views/Errors/NotFound.vue'

const router: Router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: RouteName.INDEX,
      redirect: { name: RouteName.HOME },
    },
    {
      path: '/login',
      name: RouteName.LOGIN,
      component: () => import('@/views/Lunch/LoginView.vue'),
    },
    {
      path: '/home',
      name: RouteName.HOME,
      component: () => import('@/views/Lunch/HomeView.vue'),
    },
    {
      path: '/order/:roundId',
      name: RouteName.ORDER_DAY,
      component: () => import('@/views/Lunch/OrderDayView.vue'),
      props: true,
    },
    {
      path: '/order/:roundId/confirmation',
      name: RouteName.ORDER_CONFIRMATION,
      component: () => import('@/views/Lunch/OrderConfirmationView.vue'),
      props: true,
    },
    {
      path: '/instellingen',
      name: RouteName.SETTINGS,
      component: () => import('@/views/Lunch/SettingsView.vue'),
    },

    {
      path: '/admin',
      component: () => import('@/views/Admin/AdminSection.vue'),
      redirect: { name: RouteName.ADMIN_ROUNDS },
      // Restrict the admin section and all of its child routes to users with the 'admin' role.
      beforeEnter: (): true | RouteLocationRaw => {
        const auth = useAuthStore()
        return auth.isAdmin ? true : { name: RouteName.HOME }
      },
      children: [
        {
          path: 'dashboard',
          name: RouteName.ADMIN_DASHBOARD,
          component: () => import('@/views/Admin/DashboardView.vue'),
        },
        {
          path: 'bestelrondes',
          name: RouteName.ADMIN_ROUNDS,
          component: () => import('@/views/Admin/RoundsView.vue'),
        },
        {
          path: 'bestelrondes/:roundId',
          name: RouteName.ADMIN_ROUND_DETAIL,
          component: () => import('@/views/Admin/RoundDetailView.vue'),
          props: true,
        },
        {
          path: 'bestelrondes/:roundId/:userId',
          name: RouteName.ADMIN_ROUND_USER_DETAIL,
          component: () => import('@/views/Admin/RoundsUserDetailView.vue'),
          props: true,
        },
        {
          path: 'cateraars',
          name: RouteName.ADMIN_CATERERS,
          component: () => import('@/views/Admin/CaterersView.vue'),
        },
        // {
        //   path: 'cateraars/:catererId',
        //   name: RouteName.ADMIN_CATERER_MENU,
        //   component: () => import('@/views/Admin/CatererMenuView.vue'),
        //   props: true,
        // },
        {
          path: 'kantoren',
          name: RouteName.ADMIN_OFFICES,
          meta: { isSettings: true },
          component: () => import('@/views/Admin/OfficesView.vue'),
        },
        {
          path: 'kantoren/:officeId',
          name: RouteName.ADMIN_OFFICE_DETAIL,
          meta: { isSettings: true },
          component: () => import('@/views/Admin/OfficeDetailView.vue'),
          props: true,
        },
        {
          path: 'gebruikers',
          name: RouteName.ADMIN_USERS,
          meta: { isSettings: true },
          component: () => import('@/views/Admin/UsersView.vue'),
        },
        {
          path: 'gebruikers/:userId',
          name: RouteName.ADMIN_USER_DETAIL,
          meta: { isSettings: true },
          component: () => import('@/views/Admin/UserDetailView.vue'),
          props: true,
        },
        {
          path: 'rollen',
          name: RouteName.ADMIN_ROLES,
          meta: { isSettings: true },
          component: () => import('@/views/Admin/RolesView.vue'),
        },
        {
          path: 'productlijsten',
          name: RouteName.ADMIN_PRODUCT_LISTS,
          component: () => import('@/views/Admin/ProductListsView.vue'),
        },
        {
          path: 'productlijsten/:productListId',
          name: RouteName.ADMIN_PRODUCT_LISTS_MENU,
          component: () => import('@/views/Admin/ProductListMenuView.vue'),
          props: true,
        },
        {
          path: 'lunchvoorkeuren/:date/:office?',
          name: RouteName.ADMIN_DAY_PREFERENCES,
          component: () => import('@/views/Admin/DayPreferencesView.vue'),
          props: true,
        },
      ],
    },

    {
      path: '/:pathMatch(.*)',
      name: RouteName.NOT_FOUND,
      component: NotFound,
      props: { code: 404, title: 'Pagina niet gevonden', description: 'Deze pagina bestaat niet.', container: false },
    },
  ],
})

router.beforeEach(
  RouteMiddlewareFactory({
    groups: {
      public: [InitializeAppMiddleware],
      private: [InitializeAppMiddleware],
      guest: [InitializeAppMiddleware],
      unknown: [],
    },
    after: [InitializeSettingsMiddleware, VersionMiddleware, ReadyMiddleware],
  }),
)

// Recover from a stale tab whose lazy route chunks no longer exist after a deploy.
router.onError(handleDynamicImportError)
router.afterEach(() => clearDynamicImportReloadGuard())

export const getRouteName = (): RouteName => {
  return String(router.currentRoute.value.name) as RouteName
}

export const isRoute = (route: RouteName | RouteName[]): boolean => {
  route = Array.isArray(route) ? route : [route]
  return route.includes(getRouteName())
}

export const getRouteParameter = (name: string): string | undefined => {
  // eslint-disable-next-line security/detect-object-injection
  return router.currentRoute.value.params[name] ? `${router.currentRoute.value.params[name]}` : undefined
}

export type RouteWithParameters = {
  name: RouteName
  params?: Record<string, unknown>
}

export const goToRoute = (route: RouteWithParameters): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  router.push(route as any)
}

export const useRouteName = () => {
  return computed(() => {
    const route = router.currentRoute.value
    return route.name as RouteName
  })
}

export const useIsRoute = (routes: RouteName | RouteName[]) => {
  routes = Array.isArray(routes) ? routes : [routes]

  return computed(() => {
    const route = router.currentRoute.value
    return route.name ? routes.includes(route.name as RouteName) : false
  })
}

export const useRouteParameter = (name: string) => {
  return computed(() => {
    const route = router.currentRoute.value
    // eslint-disable-next-line security/detect-object-injection
    return route.params[name] ? String(route.params[name]) : undefined
  })
}

export default router
